import { useEffect, useMemo, useState } from 'react'
import { Form, Popover, Radio, Spin } from 'antd'
import { GraviButton, Horizontal, Texto, Vertical } from '@gravitate-js/excalibrr'
import type { AccessorialContext } from './types'
import { AccessorialForm } from './components/AccessorialForm'
import { BaseFreightForm } from './components/BaseFreightForm'
import { useAddFreightLineItemForm } from './hooks/useAddFreightLineItemForm'
import type { InvoiceDetail, InvoiceTransaction, TransactionDetail, TransactionRow } from '@/api/useFreight/types'
import type { GridApi } from 'ag-grid-community'
import { startCase } from 'lodash'
import { type FLIFormType, FLIFormTypes, type FormActionType, FormActionTypes } from './types'
import type { RadioChangeEvent } from 'antd/lib'
import { useAddAccessorialForm } from './hooks/useAddAccesorialForm'
import { useAddBaseFreightForm } from './hooks/useAddBaseFreightForm'
import { useMasterMetadata } from '@/api/useMetadata/useMasterMetadata'
import { createOptionsByIdAndName } from './utils'

export type FreightLineItemPopoverContentProps = {
  actionType: FormActionType
  formType: FLIFormType
  gridApi: GridApi
  fliDetail?: InvoiceDetail | TransactionDetail
  order?: TransactionRow | InvoiceTransaction
  context?: AccessorialContext
  isCreditRebillMode?: boolean
  setFormType: (type: FLIFormType) => void
  onClose: () => void
}

function FreightLineItemPopoverContent({
  actionType,
  formType,
  gridApi,
  fliDetail,
  order,
  context,
  isCreditRebillMode,
  setFormType,
  onClose,
}: FreightLineItemPopoverContentProps) {
  const bookType = useMemo(() => {
    if (actionType === 'create') {
      if (context?.bookType) return context.bookType
      if (order?.book_type) return order.book_type
      const hasCost = order?.details?.some((detail) => detail.book_type === 'Cost')
      const hasRevenue = order?.details?.some((detail) => detail.book_type === 'Revenue')

      const useCarrierDefault = hasCost && !hasRevenue
      if (hasCost && hasRevenue) return 'Revenue'
      return useCarrierDefault ? 'Cost' : 'Revenue'
    }
    return fliDetail?.book_type || 'Revenue'
  }, [context?.bookType, order?.details, order?.book_type, fliDetail?.book_type, actionType])

  function handleClose() {
    onClose()
    setFormType(FLIFormTypes.accessorial)
  }

  const accessorialFormHook = useAddAccessorialForm(
    bookType,
    actionType,
    order,
    fliDetail,
    gridApi,
    handleClose,
    context,
    isCreditRebillMode
  )
  const baseFreightFormHook = useAddBaseFreightForm(
    bookType,
    actionType,
    order,
    fliDetail,
    gridApi,
    handleClose,
    context,
    isCreditRebillMode
  )

  const { data: terminalsList = { locations: [] }, isLoading: isTerminalsLoading } =
    useMasterMetadata('terminal_options')
  const originOptions = useMemo(() => createOptionsByIdAndName(terminalsList.locations), [terminalsList.locations])

  const { data: storesList = { locations: [] }, isLoading: isStoresLoading } = useMasterMetadata('store_options')
  const destinationOptions = useMemo(() => createOptionsByIdAndName(storesList.locations), [storesList.locations])

  const showAccessorialForm = useMemo(() => {
    return actionType === FormActionTypes.create
      ? formType === FLIFormTypes.accessorial
      : actionType === FormActionTypes.edit && (fliDetail?.type === 'Accessorial' || fliDetail?.type === 'Surcharge')
  }, [actionType, formType, fliDetail?.type])

  function handleCancel() {
    handleClose()
    accessorialFormHook.resetForm()
    baseFreightFormHook.resetForm()
  }

  if (showAccessorialForm) {
    return (
      <AccessorialForm
        form={accessorialFormHook.form}
        accessorialOptions={accessorialFormHook.accessorialOptions}
        terminalOptions={originOptions}
        storeOptions={destinationOptions}
        isTerminalsLoading={isTerminalsLoading}
        isStoresLoading={isStoresLoading}
        isAccessorialsLoading={accessorialFormHook.isAccessorialsLoading}
        onFinish={accessorialFormHook.handleSubmit}
        onReset={handleCancel}
        initialValues={accessorialFormHook.initialValues}
        currentAccessorial={fliDetail}
        isAccessorialMutationPending={accessorialFormHook.isAccessorialMutationPending}
      />
    )
  }
  return (
    <BaseFreightForm
      form={baseFreightFormHook.form}
      bookType={bookType}
      isProductOptionsLoading={baseFreightFormHook.isProductsLoading}
      order={order}
      fliDetail={fliDetail}
      originOptions={originOptions}
      destinationOptions={destinationOptions}
      isTerminalsLoading={isTerminalsLoading}
      isStoresLoading={isStoresLoading}
      productOptions={baseFreightFormHook.productOptions}
      isBaseFreightMutationPending={baseFreightFormHook.isBaseFreightMutationPending}
      isCreating={actionType === FormActionTypes.create}
      onFinish={baseFreightFormHook.handleSubmit}
      onReset={handleCancel}
      initialValues={baseFreightFormHook.initialValues}
    />
  )
}

export type AddFreightLineItemPopoverProps = {
  actionType: FormActionType
  gridApi: GridApi
  fliDetail?: InvoiceDetail | TransactionDetail | undefined
  order?: TransactionRow | InvoiceTransaction | undefined
  children: React.ReactNode
  isCreditRebillMode?: boolean
  context?: AccessorialContext
}

export function AddFreightLineItemPopover(props: AddFreightLineItemPopoverProps): JSX.Element | null {
  const [open, setOpen] = useState(false)
  const [formType, setFormType] = useState<FLIFormType>(FLIFormTypes.accessorial)

  if (props.actionType === FormActionTypes.create) {
    const shouldShowPopover = !props.order?.details?.some((d) => d.status === 'posted') || props.isCreditRebillMode

    if (!shouldShowPopover) {
      return null
    }
  }

  function handleFormChange(e: RadioChangeEvent) {
    setFormType(e.target.value as FLIFormType)
  }
  const isSurcharge = props.fliDetail?.type === 'Surcharge'

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      destroyTooltipOnHide
      title={
        <PopoverTitle
          actionType={props.actionType}
          formType={props.actionType === FormActionTypes.create ? formType : props.fliDetail?.type || ''}
          handleFormChange={handleFormChange}
        />
      }
      trigger='click'
      placement={'bottomRight'}
      styles={{ body: { minWidth: '280px', width: isSurcharge ? 'auto' : '400px' } }}
      content={
        <FreightLineItemPopoverContent
          actionType={props.actionType}
          formType={formType}
          fliDetail={props.fliDetail}
          order={props.order}
          context={props.context}
          gridApi={props.gridApi}
          isCreditRebillMode={props.isCreditRebillMode}
          setFormType={setFormType}
          onClose={() => setOpen(false)}
        />
      }
    >
      {props.children}
    </Popover>
  )
}

type PopoverTitleProps = {
  actionType: FormActionType
  formType: FLIFormType | string
  handleFormChange: (e: RadioChangeEvent) => void
}

function PopoverTitle({ actionType, formType, handleFormChange }: PopoverTitleProps) {
  return (
    <Horizontal justifyContent='space-between' verticalCenter className='pb-1' gap={32}>
      <Texto category='p2'>
        {actionType === FormActionTypes.create ? 'Add Freight Line Item' : `Edit ${formType}`}
      </Texto>
      {actionType === FormActionTypes.create && (
        <Radio.Group value={formType} buttonStyle='solid' onChange={handleFormChange}>
          {Object.values(FLIFormTypes).map((type) => (
            <Radio.Button key={type} value={type}>
              {startCase(type)}
            </Radio.Button>
          ))}
        </Radio.Group>
      )}
    </Horizontal>
  )
}
